import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { Check, Download, Mail, Wallet, Lock, ScanLine, FileText, Upload, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { CredentialCard, ParticipantActivity } from "@/components/credencial/CredentialCard";
import { ScanViewport } from "@/components/scan/ScanViewport";
import { fullName, qrValue, downloadBlob } from "@/lib/demo/utils";
import { api, apiError, fetchBlobUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n/i18n";

function saveCredentialImage(qrBox, p) {
  const qr = qrBox?.querySelector("canvas");
  if (!qr) return;
  const c = document.createElement("canvas");
  c.width = 720; c.height = 1040;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 720, 1040);
  ctx.fillStyle = "#8B0000"; ctx.fillRect(0, 0, 720, 110);
  ctx.fillStyle = "#fff"; ctx.font = "bold 34px Outfit, sans-serif"; ctx.fillText("FORO DE EMPLEO UPV 2027", 48, 70);
  ctx.fillStyle = "#0F172A"; ctx.font = "bold 40px Outfit, sans-serif"; ctx.fillText(fullName(p).toUpperCase(), 48, 180);
  ctx.fillStyle = "#475569"; ctx.font = "26px sans-serif"; ctx.fillText(`${p.titulacion} · ${p.centro}`, 48, 225);
  ctx.drawImage(qr, 110, 270, 500, 500);
  ctx.fillStyle = "#0F172A"; ctx.font = "bold 36px monospace"; ctx.textAlign = "center"; ctx.fillText(p.token, 360, 840);
  ctx.font = "bold 26px monospace"; ctx.fillText("12–13 MAYO 2027", 360, 900);
  ctx.fillStyle = "#94A3B8"; ctx.font = "20px sans-serif"; ctx.fillText("Technology by Do IT Happen", 360, 1000);
  c.toBlob((b) => b && downloadBlob(b, `Credencial_${p.token}.png`));
}

/** Option B: the participant reads the QR shown at the company stand. */
const CompanyQrDialog = ({ open, onClose }) => {
  const nav = useNavigate();
  const { t } = useI18n();
  const done = useRef(false);
  useEffect(() => { if (open) done.current = false; }, [open]);
  const onDecode = useCallback((text) => {
    if (done.current) return;
    const m = String(text).toUpperCase().match(/([A-Z0-9]{2,10}-E-[A-Z0-9]{8})/);
    if (!m) { toast.error(t("Este QR no es de una empresa del Foro")); return; }
    done.current = true;
    onClose();
    nav(`/q/${m[1]}`);
  }, [nav, onClose, t]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-slate-950 text-white" data-testid="company-qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{t("ESCANEA EL QR DE LA EMPRESA")}</DialogTitle>
          <DialogDescription className="text-white/60">{t("Apunta al código del stand para compartir tu currículum con esa empresa.")}</DialogDescription>
        </DialogHeader>
        {open && <ScanViewport phase="idle" cameraOn onDecode={onDecode} />}
      </DialogContent>
    </Dialog>
  );
};

export default function Credencial() {
  const { participant: p, setParticipant, settings } = useAuth();
  const qrRef = useRef(null);
  const cvInput = useRef(null);
  const { t } = useI18n();
  const [activity, setActivity] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [cvBusy, setCvBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/participants/me/activity").then((r) => alive && setActivity(r.data.items)).catch(() => alive && setActivity([]));
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!p) return null;
  const save = () => { saveCredentialImage(qrRef.current, p); toast.success(t("Credencial guardada"), { description: t("Imagen PNG descargada en tu dispositivo.") }); };
  const email = () => toast(t("Envío por email"), { description: t("Pendiente: el envío de emails se activará cuando la UPV confirme el proveedor de correo.") });
  const wallet = () => toast(t("Añadir a Wallet"), { description: t("Disponible en la versión final (Apple Wallet / Google Wallet).") });
  const viewCv = async () => {
    try { const u = await fetchBlobUrl("/participants/me/cv"); window.open(u, "_blank", "noopener"); } catch (e) { toast.error(t(apiError(e).message)); }
  };
  const replaceCv = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("El CV supera el tamaño máximo (5 MB)")); return; }
    const fd = new FormData();
    fd.append("cv", file, file.name);
    setCvBusy(true);
    try { const { data } = await api.put("/participants/me/cv", fd); setParticipant(data); toast.success(t("Currículum actualizado")); } catch (e) { toast.error(t(apiError(e).message)); } finally { setCvBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="credencial-page">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 py-12 lg:px-10 lg:py-16">
        <div className="fade-up mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Registro completado")}</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="credencial-greeting">{t("¡TODO LISTO, {name}!", { name: p.firstName.toUpperCase() })}</h1>
          <p className="mt-3 text-lg text-slate-600">{t("Tu acceso al Foro de Empleo UPV 2027")}</p>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:gap-14">
          <div className="fade-up-1 mx-auto w-full min-w-0 max-w-[440px]"><CredentialCard ref={qrRef} participant={p} /></div>
          <div className="fade-up-2 space-y-6">
            <p className="font-display text-2xl font-semibold leading-snug tracking-tight sm:text-3xl" data-testid="credencial-message">{t("“Este código es tu identificación durante el Foro.”")}</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {["Acceso al recinto", "Compartir CV", "Interacción con empresas"].map((l, i) => (
                <div key={l} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`credencial-indicator-${i + 1}`}>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-4 w-4" strokeWidth={3} /></span>
                  <span className="text-sm font-semibold">{t(l)}</span>
                </div>
              ))}
            </div>
            {settings.optionB && (
              <div className="rounded-[28px] bg-slate-950 p-5 text-white" data-testid="credencial-optionb-card">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">{t("Recomendado: escanea el QR del stand para compartir tu CV.")}</p>
                <button onClick={() => setScanOpen(true)} className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark" data-testid="credencial-scan-company-button"><ScanLine className="h-5 w-5" /> {t("ESCANEAR QR DE EMPRESA")}</button>
              </div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row">
              <button onClick={save} className="flex h-14 shrink-0 items-center sm:flex-1 justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark" data-testid="credencial-save-button"><Download className="h-4 w-4" /> {t("GUARDAR CREDENCIAL")}</button>
              <button onClick={email} className="flex h-14 shrink-0 items-center sm:flex-1 justify-center gap-2 rounded-2xl border-2 border-slate-900 font-display font-bold tracking-wider transition-colors hover:bg-slate-900 hover:text-white" data-testid="credencial-email-button"><Mail className="h-4 w-4" /> {t("ENVIAR POR EMAIL")}</button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button onClick={wallet} className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:px-6" data-testid="credencial-wallet-button"><Wallet className="h-4 w-4" /> {t("Añadir a Wallet")}</button>
            </div>
            <div className="flex flex-col gap-3 rounded-[28px] border border-slate-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="credencial-cv-card">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-upv-light text-upv"><FileText className="h-5 w-5" /></span>
                <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("Tu currículum")}</p><p className="truncate font-mono text-sm font-semibold" data-testid="credencial-cv-filename">{p.cvFileName}</p></div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={viewCv} className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold hover:border-upv hover:text-upv" data-testid="credencial-cv-view-button">{t("VER CV")}</button>
                <button onClick={() => cvInput.current?.click()} disabled={cvBusy} className="flex items-center gap-1.5 rounded-xl bg-slate-950 px-4 py-2 text-xs font-bold text-white disabled:opacity-60" data-testid="credencial-cv-replace-button">{cvBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {t("Actualizar")}</button>
                <input ref={cvInput} type="file" accept="application/pdf" className="hidden" onChange={(e) => { replaceCv(e.target.files?.[0]); e.target.value = ""; }} data-testid="credencial-cv-file-input" />
              </div>
            </div>
            <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="credencial-privacy">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Lock className="h-3.5 w-3.5" /> {t("Qué contiene tu QR")}</div>
              <p className="mt-3 break-all font-mono text-sm font-semibold text-slate-900" data-testid="credencial-qr-value">{qrValue(p.token)}</p>
              <p className="mt-2 text-sm text-slate-500">{t("Solo un token seguro. Tu nombre, email, teléfono y CV nunca viajan dentro del código: la plataforma los asocia de forma protegida.")}</p>
            </div>
            <Link to="/empresas" className="flex h-12 items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-700 transition-colors hover:border-upv hover:text-upv" data-testid="credencial-companies-link">{t("Ver empresas participantes")} →</Link>
            <ParticipantActivity items={activity} />
          </div>
        </div>
      </main>
      <CompanyQrDialog open={scanOpen} onClose={() => setScanOpen(false)} />
      <PageFooter />
    </div>
  );
}
